/**
 * Réglages d'exécution de l'application.
 *
 * Deux sources, dans cet ordre : le fichier `/config.json` servi à côté de l'application, puis les
 * variables `VITE_*` figées au moment du build.
 *
 * La première est celle du cluster. L'image web est construite une fois et déployée partout ; ce
 * qu'elle doit savoir d'un déploiement — l'émetteur des jetons, le client OpenID — n'est connu que
 * du déploiement lui-même, et le chart le pose dans un ConfigMap monté sur ce chemin. Le figer au
 * build donnerait une image par cluster, ou pire : une image sans configuration qui démarre quand
 * même, ce qui est exactement ce que le premier déploiement a montré.
 *
 * La seconde est celle du poste de développement : Aspire injecte les variables au démarrage de Vite,
 * et les tests posent `VITE_AUTH_MODE=test`. Aucun fichier `/config.json` n'existe là.
 */

export interface RuntimeConfig {
  /** Autorité OpenID du realm « datamaster ». Vide ⇒ l'application refuse de démarrer hors mode test. */
  readonly keycloakAuthority: string;
  /** Client public PKCE de l'application web. */
  readonly keycloakClientId: string;
  /** Origine de la passerelle. Vide ⇒ même origine (cluster) ou proxy Vite (poste). */
  readonly gatewayUrl: string;
  /**
   * Adresse d'où le cabinet télécharge l'outil de synchronisation — le fichier App Installer servi
   * sous `/agent/` par ce même déploiement (ADR-0024).
   *
   * Vide ⇒ ce déploiement ne sert aucun canal, et l'interface n'OFFRE pas le téléchargement :
   * l'application n'a aucun autre moyen de savoir si `/agent/…` répond, et un bouton qui mène à un
   * 404 est un geste qui ne fait rien (ADR-0016 § 2, choix 18). Vide aussi sur un poste de
   * développement, où aucun `/config.json` n'existe.
   */
  readonly agentDownloadUrl: string;
  /**
   * Adresse de la moitié publique du certificat qui a signé le paquet, quand le cabinet doit
   * l'APPROUVER avant d'installer (ADR-0026).
   *
   * Non vide seulement lorsque la racine du certificat est inconnue de Windows — un certificat
   * auto-signé. L'interface montre alors la marche supplémentaire, AVANT le téléchargement : sans
   * elle, le clic sur l'installeur échoue en « impossible d'installer » et rien n'indique quoi faire.
   *
   * Vide avec un certificat qui chaîne jusqu'à une racine publique : il n'y a rien à approuver, et
   * offrir le geste ferait faire au cabinet une manipulation en administrateur pour rien. Vide aussi
   * quand ce déploiement ne sert aucun canal, et sur un poste de développement.
   */
  readonly agentCertificateUrl: string;
}

/** Chemin du fichier de réglages, servi sans cache par nginx (deploy/docker/nginx.conf). */
export const runtimeConfigPath = '/config.json';

function fromBuild(): RuntimeConfig {
  return {
    keycloakAuthority: import.meta.env.VITE_KEYCLOAK_AUTHORITY ?? '',
    keycloakClientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID ?? '',
    gatewayUrl: import.meta.env.VITE_GATEWAY_URL ?? '',
    agentDownloadUrl: import.meta.env.VITE_AGENT_DOWNLOAD_URL ?? '',
    agentCertificateUrl: import.meta.env.VITE_AGENT_CERTIFICATE_URL ?? '',
  };
}

function readString(source: Record<string, unknown>, key: string): string | undefined {
  const value = source[key];
  return typeof value === 'string' ? value : undefined;
}

/** Fusionne un fichier partiel avec les valeurs du build : une clé absente ne vide pas une valeur connue. */
export function mergeRuntimeConfig(base: RuntimeConfig, file: unknown): RuntimeConfig {
  if (file === null || typeof file !== 'object') {
    return base;
  }

  const source = file as Record<string, unknown>;
  return {
    keycloakAuthority: readString(source, 'keycloakAuthority') ?? base.keycloakAuthority,
    keycloakClientId: readString(source, 'keycloakClientId') ?? base.keycloakClientId,
    gatewayUrl: readString(source, 'gatewayUrl') ?? base.gatewayUrl,
    agentDownloadUrl: readString(source, 'agentDownloadUrl') ?? base.agentDownloadUrl,
    agentCertificateUrl: readString(source, 'agentCertificateUrl') ?? base.agentCertificateUrl,
  };
}

let current: RuntimeConfig = fromBuild();

/** Réglages courants. Avant `loadRuntimeConfig`, ce sont ceux du build. */
export function runtimeConfig(): RuntimeConfig {
  return current;
}

/**
 * Charge `/config.json` s'il existe. Un fichier absent (poste de développement) ou illisible laisse
 * les valeurs du build ; un fichier présent les recouvre clé par clé.
 */
export async function loadRuntimeConfig(fetchImpl: typeof fetch = globalThis.fetch): Promise<RuntimeConfig> {
  const base = fromBuild();
  try {
    const response = await fetchImpl(runtimeConfigPath, { cache: 'no-store' });
    if (!response.ok) {
      current = base;
      return current;
    }

    current = mergeRuntimeConfig(base, await response.json());
  } catch {
    current = base;
  }

  return current;
}

/** Réservé aux tests : impose des réglages sans passer par le réseau. */
export function setRuntimeConfigForTests(config: Partial<RuntimeConfig>): void {
  current = { ...fromBuild(), ...config };
}
